import { createSelector } from 'reselect';
import {
  find,
  path,
  pipe,
  prop,
  propEq,
  head,
  values,
  concat,
  is,
  propOr,
  map,
  pathOr,
  length,
  split,
  trim,
  last,
  test,
  filter,
  toLower,
} from 'ramda';

import i18n from 'services/i18n';
import { NA } from 'constants/common';
import { getAccumulatorSeparatorWithNA, formatDate } from 'utilities/formatters';

const localState = prop('patientQuestionnaireResponse');

export const getPatientQuestionnaireResponseData = createSelector(
  localState,
  prop('data'),
);

export const getPatientQuestionnaireResponseLoading = createSelector(
  localState,
  prop('loading'),
);

export const getPatientQuestionnaireResponseItem = createSelector(
  localState,
  pathOr([], ['data', 'item']),
);

export const getPatientQuestionnaireResponseMedications = createSelector(
  localState,
  pipe(
    path(['data', 'item']),
    find(propEq('linkId', 'medications')),
  ),
);

export const getPatientsMedicationsInfoBlock = createSelector(
  getPatientQuestionnaireResponseMedications,
  pipe(
    propOr([], 'item'),
    map(({ item }) => ({
      value: item.reduce((acc, current, index) => {
        if (!current.answer) {
          return acc;
        }

        const answerValues = pipe(
          propOr([], 'answer'),
          head,
          values,
          head,
        )(current);

        const accWithSeparator = getAccumulatorSeparatorWithNA(acc, index);

        if (is(Object, answerValues)) {
          return concat(accWithSeparator, i18n.t(answerValues.display));
        }

        return concat(accWithSeparator, answerValues);
      }, ''),
    })),
  ),
);

export const getPatientQuestionnaireResponseDeviceImplant = createSelector(
  getPatientQuestionnaireResponseItem,
  pipe(
    find(propEq('linkId', 'deviceimplant')),
    pathOr(NA, ['answer', '0', 'valueCoding', 'display']),
  ),
);

export const getPatientQuestionnaireResponseDeviceImplantDate = createSelector(
  getPatientQuestionnaireResponseItem,
  pipe(
    find(propEq('linkId', 'device_do_implant')),
    path(['answer', '0', 'valueDate']),
    valueDate => (valueDate ? formatDate(i18n.t('dateTimeFormat.dateOfImplant'))(valueDate) : NA),
  ),
);
export const getPatientQuestionnaireResponseDeviceClinicSpecial = createSelector(
  getPatientQuestionnaireResponseItem,
  pipe(
    find(propEq('linkId', 'device_follow')),
    propOr([], 'item'),
    find(propEq('linkId', 'device_clinic_special')),
    pathOr(NA, ['answer', '0', 'valueString']),
  ),
);

export const getPatientQuestionnaireResponseSatelliteDeviceClinic = createSelector(
  getPatientQuestionnaireResponseItem,
  pipe(
    find(propEq('linkId', 'device_follow')),
    propOr([], 'item'),
    find(propEq('linkId', 'device_satname')),
    pathOr(NA, ['answer', '0', 'valueString']),
  ),
);

export const getPatientQuestionnaireResponseLocalDeviceClinicPhone = createSelector(
  getPatientQuestionnaireResponseItem,
  pipe(
    find(propEq('linkId', 'device_phone_urgent')),
    pathOr(NA, ['answer', '0', 'valueString']),
  ),
);

export const getPatientQuestionnaireResponseDeviceVendor = createSelector(
  getPatientQuestionnaireResponseItem,
  pipe(
    find(propEq('linkId', 'manufacturer')),
    pathOr(NA, ['answer', '0', 'valueCoding', 'display']),
  ),
);

export const getPatientQuestionnaireResponseDeviceName = createSelector(
  getPatientQuestionnaireResponseItem,
  pipe(
    find(propEq('linkId', 'model_name')),
    pathOr(NA, ['answer', '0', 'valueString']),
    split('|'),
    head,
    trim,
  ),
);

export const getPatientQuestionnaireResponseSerialNumber = createSelector(
  getPatientQuestionnaireResponseItem,
  pipe(
    find(propEq('linkId', 'model_serialnum')),
    pathOr(NA, ['answer', '0', 'valueString']),
    split('|'),
    last,
    trim,
  ),
);

export const getPatientQuestionnaireResponseActiveImplantsCount = createSelector(
  getPatientQuestionnaireResponseItem,
  pipe(
    filter(({ linkId, answer }) => test(/lead_active_\d+$/, linkId) && answer),
    length,
  ),
);

export const getPatientQuestionnaireResponseInactiveImplantsCount = createSelector(
  getPatientQuestionnaireResponseItem,
  pipe(
    filter(({ linkId, answer }) => test(/lead_inactive_\d+$/, linkId) && answer),
    length,
  ),
);

export const getPatientQuestionnaireResponseHomeMonitor = createSelector(
  getPatientQuestionnaireResponseItem,
  pipe(
    find(propEq('linkId', 'home_monitor')),
    path(['answer', '0', 'valueCoding', 'display']),
    monitor => (monitor ? i18n.t(toLower(monitor)) : NA),
  ),
);

export const getPatientClinicName = createSelector(
  getPatientQuestionnaireResponseItem,
  pipe(
    find(propEq('linkId', 'device_remotetransclin')),
    path(['answer', '0', 'valueString']),
    clinic => clinic || i18n.t('reports.missingClinic'),
  ),
);

export const getPatientClinicPhone = createSelector(
  getPatientQuestionnaireResponseItem,
  pipe(
    find(propEq('linkId', 'device_phone_urgent')),
    pathOr(NA, ['answer', '0', 'valueString']),
  ),
);
